import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiService from '../../../services/apiService';
import { Button } from '../../../components/ui';
import { FaPencilAlt, FaArrowLeft } from 'react-icons/fa';
import { useToast } from '../../../context/ToastContext';

const breadcrumb = (
  <span>
    <span className="cursor-pointer hover:text-blue-600" onClick={() => window.history.back()}>
      Home
    </span> &nbsp;/&nbsp; 
    <span className="cursor-pointer hover:text-blue-600" onClick={() => window.history.back()}>
      Course Programs
    </span> &nbsp;/&nbsp; 
    <span className="text-gray-700">View</span>
  </span>
);

const ViewCourseProgram = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const toast = useToast();
  const [courseProgram, setCourseProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('basic');

  useEffect(() => {
    const fetchCourseProgram = async () => {
      try {
        setLoading(true);
        const data = await apiService.request(`/i/ims/courseprogram/${id}?populate=course,eduStandard`);
        setCourseProgram(data);
      } catch (error) {
        console.error('Error fetching course program:', error);
        toast.showError('Failed to fetch course program details');
        navigate('/course-programs');
      } finally {
        setLoading(false);
      }
    };

    fetchCourseProgram();
  }, [id, navigate]);

  if (loading || !courseProgram) {
    return (
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return dateString;
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="border-b border-gray-200">
        <div className="p-6">
          <nav className="text-xs text-gray-500 mb-4" aria-label="Breadcrumb">
            {breadcrumb}
          </nav>
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-2xl font-bold text-gray-900">{courseProgram.name}</h1>
            <div className="flex space-x-2">
              <Button
                onClick={() => navigate('/course-programs')}
                variant="outline"
                size="md"
                icon={FaArrowLeft}
                className="border-gray-300 text-gray-700"
              >
                Back
              </Button>
              <Button
                onClick={() => navigate(`/course-programs/edit/${id}`)}
                variant="outline"
                size="md"
                icon={FaPencilAlt}
                className="border-amber-500 text-amber-600 hover:bg-amber-50"
              >
                Edit
              </Button>
            </div>
          </div>
          
          {/* Status Badge */}
          <div className="mb-6">
            <span className={`px-3 py-1 rounded-full text-sm ${courseProgram.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
              {courseProgram.status ? 'Active' : 'Inactive'}
            </span>
          </div>
          
          {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px space-x-8">
              <button
                onClick={() => setActiveTab('basic')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'basic'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Basic Details
              </button>
              <button
                onClick={() => setActiveTab('meta')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'meta'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Meta Tags
              </button>
            </nav>
          </div>
        </div>
        
        <div className="p-6">
          {activeTab === 'basic' && (
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Name</h3>
                  <p className="text-base text-gray-900">{courseProgram.name || '-'}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">URI</h3>
                  <p className="text-base text-gray-900">{courseProgram.uri || '-'}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Course</h3>
                  <p className="text-base text-gray-900">
                    {courseProgram.course ? courseProgram.course.name : '-'}
                  </p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Education Standard</h3>
                  <p className="text-base text-gray-900">
                    {courseProgram.eduStandard ? courseProgram.eduStandard.name : '-'}
                  </p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Order</h3>
                  <p className="text-base text-gray-900">{courseProgram.order || '0'}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Link</h3>
                  <p className="text-base text-gray-900">
                    {courseProgram.link ? (
                      <a 
                        href={courseProgram.link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {courseProgram.link}
                      </a>
                    ) : '-'}
                  </p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Start Date</h3>
                  <p className="text-base text-gray-900">{formatDate(courseProgram.startDate)}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">End Date</h3>
                  <p className="text-base text-gray-900">{formatDate(courseProgram.endDate)}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Cost</h3>
                  <p className="text-base text-gray-900">{courseProgram.cost || '-'}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">MRP</h3>
                  <p className="text-base text-gray-900">{courseProgram.MRP || '-'}</p>
                </div>
              </div>
              
              {/* Description */}
              <div className="col-span-2">
                <h3 className="text-sm font-medium text-gray-500 mb-1">Description</h3>
                <p className="text-base text-gray-900 whitespace-pre-wrap">
                  {courseProgram.desci || '-'}
                </p>
              </div>
              
              {/* Media */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Thumbnail</h3>
                  {courseProgram.thumb ? (
                    <div className="border rounded-md p-2 w-full max-w-xs">
                      <img 
                        src={courseProgram.thumb} 
                        alt="Thumbnail" 
                        className="w-full h-auto object-cover rounded"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/150?text=No+Image';
                        }}
                      />
                    </div>
                  ) : (
                    <p className="text-gray-500 italic">No thumbnail</p>
                  )}
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Icon</h3>
                  {courseProgram.icon ? (
                    <div className="border rounded-md p-2 w-16 h-16">
                      <img 
                        src={courseProgram.icon} 
                        alt="Icon" 
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/50?text=Icon';
                        }}
                      />
                    </div>
                  ) : (
                    <p className="text-gray-500 italic">No icon</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'meta' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">OG Title</h3>
                  <p className="text-base text-gray-900">{courseProgram.metaTags?.ogTitle || '-'}</p>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Keywords</h3>
                  <p className="text-base text-gray-900">{courseProgram.metaTags?.keywords || '-'}</p>
                </div>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-1">OG Description</h3>
                <p className="text-base text-gray-900 whitespace-pre-wrap">
                  {courseProgram.metaTags?.ogDescription || '-'}
                </p>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">OG Image</h3>
                {courseProgram.metaTags?.ogImage ? (
                  <div className="border rounded-md p-2 w-full max-w-xs">
                    <img 
                      src={courseProgram.metaTags.ogImage} 
                      alt="OG Image" 
                      className="w-full h-auto object-cover rounded"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/150?text=No+Image';
                      }}
                    />
                  </div>
                ) : (
                  <p className="text-gray-500 italic">No OG image</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewCourseProgram;
